import axios from 'axios';
import {
  GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
  GET_FORM_COMPLIANCE_RULES_URL,
  GET_FORMS_URL,
  GET_FORM_STRUCTURE_URL,
  LIST_FORM_TRANSITION_RULES_URL,
  get_request_config
} from '@/utils/system_constants/constant';

function reset_selected_rule_state(context) {
  context.rules = [];
  context.bulk_selected_rule_id = '';
  context.assigned_rule_documents = [];
}

function has_valid_form_id(form_id) {
  return Number.isFinite(form_id) && form_id > 0;
}

function selected_rule_exists(rules, selected_rule_id) {
  return rules.some((rule) => String(rule.id) === String(selected_rule_id));
}

function reset_selected_transition_rule_state(context) {
  context.transition_rules = [];
  context.bulk_selected_transition_rule_id = '';
}

function selected_transition_rule_exists(rules, selected_rule_id) {
  return rules.some((rule) => String(rule.id) === String(selected_rule_id));
}

function clear_selected_documents(context) {
  context.documents = [];
}

export default {
  async fetch_forms() {
    this.loading = true;
    this.loading_forms = true;

    try {
      const response = await axios.get(GET_FORMS_URL, get_request_config());
      this.forms = this.extract_response_items(response.data)
        .map((form) => this.normalize_form(form))
        .filter((form) => form.id);
      await this.fetch_document_form_summaries();
      this.documents = [];
    } catch (error) {
      const response_data = this.parse_response_data(error?.response?.data || {});
      this.forms = [];
      this.document_form_summaries = [];
      this.documents = [];
      this.show_error(response_data.message || 'Failed to load forms.');
    } finally {
      this.loading = false;
      this.loading_forms = false;
    }
  },
  async fetch_document_form_summaries() {
    const document_forms = this.selectable_forms;
    if (!document_forms.length) {
      this.document_form_summaries = [];
      return;
    }

    this.document_form_summaries = await Promise.all(
      document_forms.map((form) => this.fetch_document_form_summary(form))
    );
  },
  async fetch_document_form_summary(form) {
    const response = await axios.get(
      GET_FORM_STRUCTURE_URL,
      get_request_config({ params: { form_id: form.id } })
    ).catch(() => null);
    if (!response) return this.empty_document_form_summary(form);
    return this.build_document_form_summary(form, response.data);
  },
  build_document_form_summary(form, payload) {
    const form_structure = this.extract_response_items(payload);
    const documents = this.extract_document_questions(form_structure, form);
    return Object.assign({}, form, {
      document_count: documents.length,
      section_count: form_structure.length,
      workflow_count: 1
    });
  },
  empty_document_form_summary(form) {
    return Object.assign({}, form, {
      document_count: 0,
      section_count: 0,
      workflow_count: 0
    });
  },
  async fetch_rules_for_selected_form() {
    const form_id = Number(this.selected_form_id);
    if (!has_valid_form_id(form_id)) {
      reset_selected_rule_state(this);
      return;
    }

    this.loading_rules = true;

    try {
      const response = await axios.get(
        GET_FORM_COMPLIANCE_RULES_URL,
        get_request_config({ params: { form_id } })
      );
      this.rules = this.extract_rules_response_items(response.data)
        .map((rule) => this.normalize_rule(rule))
        .filter((rule) => rule.id);

      if (!selected_rule_exists(this.rules, this.bulk_selected_rule_id)) {
        this.bulk_selected_rule_id = '';
        this.assigned_rule_documents = [];
      }
    } catch {
      reset_selected_rule_state(this);
    } finally {
      this.loading_rules = false;
    }
  },
  async fetch_transition_rules_for_selected_form() {
    const form_id = Number(this.selected_form_id);
    if (!has_valid_form_id(form_id)) {
      reset_selected_transition_rule_state(this);
      return;
    }

    this.loading_transition_rules = true;

    try {
      const response = await axios.get(
        LIST_FORM_TRANSITION_RULES_URL,
        get_request_config({ params: { form_id } })
      );
      this.transition_rules = this.extract_response_items(response.data)
        .map((rule) => this.normalize_transition_rule(rule))
        .filter((rule) => rule.id);

      if (!selected_transition_rule_exists(this.transition_rules, this.bulk_selected_transition_rule_id)) {
        this.bulk_selected_transition_rule_id = '';
      }
    } catch {
      reset_selected_transition_rule_state(this);
      this.show_error('Failed to load transition rules for the selected form.');
    } finally {
      this.loading_transition_rules = false;
    }
  },
  async fetch_documents_for_all_rules() {
    if (!this.rules.length) {
      this.all_rule_documents = [];
      return;
    }

    const results = await Promise.all(
      this.rules.map((rule) => this.fetch_documents_for_rule_assignment(rule))
    );

    this.all_rule_documents = results.flat();
  },
  async fetch_documents_for_rule_assignment(rule) {
    const response = await axios.get(
      GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
      get_request_config({
        params: {
          compliance_rule_id: rule.id
        }
      })
    ).catch(() => null);

    if (!response) return [];

    const assigned_tree = this.extract_response_items(response.data);
    return this.flatten_assigned_document_questions(assigned_tree).map((document) => ({
      ...document,
      rule_id: Number(rule.id),
      rule_name: rule.name
    }));
  },
  async fetch_documents_for_selected_form() {
    const form_id = Number(this.selected_form_id);
    if (!has_valid_form_id(form_id)) {
      clear_selected_documents(this);
      return;
    }

    this.start_documents_loading();
    await this.load_documents_for_selected_form(form_id)
      .catch((error) => this.report_documents_fetch_error(error))
      .finally(() => this.stop_documents_loading());
  },
  start_documents_loading() {
    this.loading = true;
    this.loading_documents = true;
  },
  stop_documents_loading() {
    this.loading = false;
    this.loading_documents = false;
  },
  async load_documents_for_selected_form(form_id) {
    const response = await axios.get(
      GET_FORM_STRUCTURE_URL,
      get_request_config({ params: { form_id } })
    );
    await this.fetch_rules_for_selected_form();
    this.documents = this.extract_document_questions(this.extract_response_items(response.data));
    await this.fetch_transition_rules_for_selected_form();
    await this.fetch_documents_for_all_rules();
    this.selected_document_ids = [];
  },
  report_documents_fetch_error(error) {
    const response_data = this.parse_response_data(error?.response?.data || {});
    clear_selected_documents(this);
    this.show_error(response_data.message || 'Failed to load documents for the selected form.');
  }
};
